import SelectMui from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { useRouter } from 'next/router';
import Typography from '@mui/material/Typography';

import { useTheme } from '@mui/material/styles';
import { SelectProps } from './mui-interface';
import { Checkbox } from '../checkbox';
import { forwardRef } from 'react';
import { PrimeTooltip } from '../../../prime-tooltip';
import { PRIME_TRACKING_ROUTES } from '../../../constants';

export const Select = forwardRef(
  ({ options, children, selected = [], ...props }: SelectProps, ref) => {
    const theme = useTheme();
    const { asPath } = useRouter();
    const base = asPath.split('/')[1] || asPath;

    return (
      <SelectMui
        ref={ref}
        size="small"
        variant="outlined"
        MenuProps={{ sx: { maxHeight: 350 } }}
        {...props}
        label={null}
        {...(props.placeholder && {
          displayEmpty: true,
          renderValue: (value) => {
            if (!value) {
              return (
                <Typography sx={{ opacity: 0.5 }}>
                  {props.placeholder}
                </Typography>
              );
            }

            const selectedValues = Array.isArray(value) ? value : [value];

            return selectedValues
              .map((value: string) => {
                const option = options.find((option) => option.value === value);

                return option?.label;
              })
              .join(', ');
          },
        })}
        {...(props.multiple && {
          renderValue: (value = []) => {
            return (value as string[])
              .map((value: string) => {
                const option = options.find((option) => option.value === value);

                return option?.label;
              })
              .join(', ');
          },
        })}
        onChange={(e: any, child: any) => {
          const selected = options.find(
            (option) => option.value === e.target.value
          );

          if (selected?.exclusiveToPrime) return;

          if (props?.onChange) {
            props.onChange(e, child);
          }
        }}
        sx={{
          minWidth: 150,
          [theme.breakpoints.down('md')]: {
            minWidth: 'auto',
          },
          borderRadius: 1,
          ...props?.sx,
        }}
      >
        {props.multiple &&
          options?.map((option) => (
            <MenuItem key={option.value!} value={option.value!}>
              <Checkbox
                checked={selected.indexOf(option.value as string) > -1}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}

        {options.map((option) => (
          <MenuItem
            key={`select-${option.label}`}
            data-testid={`select-option`}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              borderBottom: `1px solid ${theme.palette.grey[100]}`,
              filter: option.exclusiveToPrime ? 'blur(1.5px)' : undefined,
            })}
            value={option.value!}
          >
            <PrimeTooltip
              userIsPrime={!option.exclusiveToPrime}
              link={PRIME_TRACKING_ROUTES.CTAHistoricalProfitability(base)}
            >
              <Typography
                sx={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  width: '100%',
                }}
              >
                {option.label}
              </Typography>
            </PrimeTooltip>
          </MenuItem>
        ))}
      </SelectMui>
    );
  }
);
